import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import Tabs from "../../components/common/Tabs/Tabs";
import MyItem from "../../components/common/MyItem/MyItem";
import PriceFilter from "../../components/common/PriceFilter/PriceFilter";
import PageSpinner from '../../components/common/PageSpinner/PageSpinner';
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp } from "react-icons/md";
import { apiFetch } from "../../utils/api";
import styles from "./DashboardPurchases.module.css";
import * as Sentry from "@sentry/react";

const DashboardPurchases = () => {
  const [ isPageLoading, setIsPageLoading ] = useState(false);
  const [myItemsData, setMyItemsData] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState([0, 10000]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeTab, setActiveTab] = useState("All");

  const [isTypesOpen, setIsTypesOpen] = useState(false);
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        setIsPageLoading(true);
        const data = await apiFetch("/api/purchases/user", {}, true);
        setMyItemsData(data);
      } catch (error) {
        Sentry.captureException(error);
        console.error("❌ Failed to fetch purchases:", error);
      } finally {
        setIsPageLoading(false);
    }
    };

    fetchPurchases();
  }, []);

  const types = [...new Set(myItemsData.map(item => item.type))];
  const tags = [...new Set(myItemsData.flatMap(item => item.tags || []))];

  const maxPrice = Math.max(...myItemsData.map(item => item.price || 0), 10000);
  const tiers = 8;
  const priceStep = Math.ceil(maxPrice / tiers);

  const staticDataValues = Array.from({ length: tiers }, (_, index) => {
    const min = index * priceStep;
    const max = (index + 1) * priceStep;
    const count = myItemsData.filter(item => item.price >= min && item.price < max).length;
    return count;
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "All") {
      setSelectedTags([]);
    } else {
      setSelectedTags([tab.toLowerCase()]);
    }
  };

  const filteredItems = myItemsData.filter(item => {
    const isPriceMatch = item.price >= selectedPriceRange[0] && item.price <= selectedPriceRange[1];
    const isTypeMatch = selectedTypes.length === 0 || selectedTypes.includes(item.type);
    const isTagMatch = selectedTags.length === 0 || (item.tags || []).some(tag => selectedTags.includes(tag));
    return isPriceMatch && isTypeMatch && isTagMatch;
  });

  // const totalItems = myItemsData.length;
  // const totalCreators = new Set(myItemsData.map(item => item.name)).size;
  // const avgPrice = myItemsData.reduce((sum, item) => sum + item.price, 0) / totalItems || 0;
  // const totalSpent = myItemsData.reduce((sum, item) => sum + item.price, 0);


  if (isPageLoading) {
    return <PageSpinner />;
  }
  
  const totalItems = filteredItems.length;
  const totalCreators = new Set(filteredItems.map(item => item.name)).size;
  const avgPrice = filteredItems.reduce((sum, item) => sum + item.price, 0) / totalItems || 0;
  const totalSpent = filteredItems.reduce((sum, item) => sum + item.price, 0);

  return (
    <Box className={styles.dashboardPurchases}>
      <Typography variant="h1" className={styles.header}>My Purchases</Typography>

      <Box className={styles.statisticsContainer}>
        <Box className={styles.statistics}>
          <Box className={styles.statBox}>
            <span className={styles.statLabel}>Items</span>
            <span className={styles.statValue}>{totalItems}</span>
          </Box>
          <Box className={styles.statBox}>
            <span className={styles.statLabel}>Creators</span>
            <span className={styles.statValue}>{totalCreators}</span>
          </Box>
          <Box className={styles.statBox}>
            <span className={styles.statLabel}>Avg. Price</span>
            <span className={styles.statValue}>${avgPrice.toFixed(2)}</span>
          </Box>
          <Box className={styles.statBox}>
            <span className={styles.statLabel}>Total</span>
            <span className={styles.statValue}>${totalSpent.toLocaleString()}</span>
          </Box>
        </Box>
      </Box>

      {/* <Tabs tabs={["All", "Sport", "Art", "Music", "Photography"]} onChange={handleTabChange} /> */}

      <Box className={styles.content}>
        <Box className={styles.myItems}>
          {filteredItems.map((item, index) => (
            <MyItem key={index} {...item} />
          ))}
        </Box>

        <Box className={styles.filters}>
          <Box className={styles.filterAccordion}>
            <Box className={styles.accordionHeader} onClick={() => setIsPriceOpen(!isPriceOpen)}>
              <Typography>Price</Typography>
              {isPriceOpen ? <MdOutlineKeyboardArrowDown /> : <MdOutlineKeyboardArrowUp />}
            </Box>
            {isPriceOpen && (
              <PriceFilter
                maxPrice={maxPrice}
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                dataValues={staticDataValues}
              />
            )}
          </Box>

          <Box className={styles.filterAccordion}>
            <Box className={styles.accordionHeader} onClick={() => setIsTypesOpen(!isTypesOpen)}>
              <Typography>Types</Typography>
              {isTypesOpen ? <MdOutlineKeyboardArrowDown /> : <MdOutlineKeyboardArrowUp />}
            </Box>
            {isTypesOpen && (
              <Box className={styles.filterContent}>
                {types.map((type, index) => (
                  <Box key={index} className={styles.filterOption}>
                    <input
                      type="checkbox"
                      value={type}
                      checked={selectedTypes.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTypes([...selectedTypes, type]);
                        } else {
                          setSelectedTypes(selectedTypes.filter(t => t !== type));
                        }
                      }}
                    />
                    <label>{type}</label>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box className={styles.filterAccordion}>
            <Box className={styles.accordionHeader} onClick={() => setIsTagsOpen(!isTagsOpen)}>
              <Typography>Tags</Typography>
              {isTagsOpen ? <MdOutlineKeyboardArrowDown /> : <MdOutlineKeyboardArrowUp />}
            </Box>
            {isTagsOpen && (
              <Box className={styles.filterContent}>
                {tags.map((tag, index) => (
                  <Box key={index} className={styles.filterOption}>
                    <input
                      type="checkbox"
                      value={tag}
                      checked={selectedTags.includes(tag)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTags([...selectedTags, tag]);
                        } else {
                          setSelectedTags(selectedTags.filter(t => t !== tag));
                        }
                      }}
                    />
                    <label>{tag}</label>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPurchases;